import { useState } from 'react';
import { Tag, X } from 'lucide-react';
import type { Campaign } from '@/types';
import { SectionTitle } from '@atoms/SectionTitle';
import { Button } from '@atoms/Button';
import { formatCampaignCondition } from '@/utils/categoryIcons';
import { mediaUrl } from '@/utils/mediaUrl';

interface CampaignCarouselProps {
  campaigns: Campaign[];
}

export function CampaignCarousel({ campaigns }: CampaignCarouselProps) {
  const [selected, setSelected] = useState<Campaign | null>(null);
  if (!campaigns.length) return null;

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="section-container">
        <SectionTitle
          title="Güncel "
          accent="Kampanyalar"
          subtitle="Toptancılarımızın sunduğu avantajlı fırsatları inceleyin."
        />
        <div className="grid gap-6 md:grid-cols-2">
          {campaigns.map((campaign) => {
            const image = mediaUrl(campaign.image);
            const condition = formatCampaignCondition(campaign);
            return (
              <button
                key={campaign.id}
                type="button"
                onClick={() => setSelected(campaign)}
                className="card-elevated group overflow-hidden text-left hover:-translate-y-0.5"
              >
                <div className="grid md:grid-cols-5">
                  <div className="relative h-44 overflow-hidden bg-slate-100 md:col-span-2 md:h-auto">
                    {image ? (
                      <img
                        src={image}
                        alt={campaign.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="flex h-full min-h-[11rem] items-center justify-center">
                        <Tag className="h-10 w-10 text-slate-300" strokeWidth={1.25} />
                      </div>
                    )}
                    <span className="absolute left-3 top-3 rounded-full bg-brand-500 px-2.5 py-0.5 text-xs font-semibold text-white">
                      Kampanya
                    </span>
                  </div>
                  <div className="flex flex-col justify-center p-6 md:col-span-3">
                    <h3 className="text-lg font-bold text-slate-900">{campaign.title}</h3>
                    {campaign.wholesaler?.name && (
                      <p className="mt-1 text-sm font-medium text-slate-500">{campaign.wholesaler.name}</p>
                    )}
                    <p className="mt-3 flex items-center gap-2 text-sm font-medium text-brand-600">
                      <Tag className="h-4 w-4" />
                      {condition}
                    </p>
                    <p className="mt-4 text-sm font-semibold text-slate-700 group-hover:text-brand-600">
                      Detayları Gör →
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Kapat"
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card-hover">
            <button
              type="button"
              aria-label="Kapat"
              onClick={() => setSelected(null)}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            >
              <X className="h-5 w-5" />
            </button>
            {mediaUrl(selected.image) && (
              <img src={mediaUrl(selected.image)} alt="" className="h-48 w-full object-cover" />
            )}
            <div className="space-y-3 p-6">
              <h3 className="text-xl font-bold text-slate-900">{selected.title}</h3>
              {selected.wholesaler?.name && (
                <p className="text-sm font-medium text-slate-500">{selected.wholesaler.name}</p>
              )}
              <p className="text-sm text-slate-600">{formatCampaignCondition(selected)}</p>
              <Button fullWidth variant="outline" onClick={() => setSelected(null)}>
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
